import { atom, useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo } from "react";

import { PartnerType } from "@/app/utils/partners.enum";

export interface SupportScreenContext {
  product?: PartnerType;
  proposal?: string;
  transactionId?: string;
  step?: string;
  data?: Record<string, unknown>;
}

export const PROPOSAL_DETAILS_STEP = "Detalhes da proposta";

const supportScreenContextAtom = atom<Record<string, SupportScreenContext>>({});

const isEmpty = (value: unknown) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (typeof value === "object" && Object.keys(value as object).length === 0);

const clean = (value: unknown): unknown => {
  if (value instanceof Date) return value.toISOString();
  if (typeof File !== "undefined" && value instanceof File) return undefined;
  if (Array.isArray(value)) {
    const items = value.map(clean).filter((item) => !isEmpty(item));
    return items.length ? items : undefined;
  }
  if (value && typeof value === "object") {
    const entries = Object.entries(value)
      .map(([key, item]) => [key, clean(item)] as const)
      .filter(([, item]) => !isEmpty(item));
    return entries.length ? Object.fromEntries(entries) : undefined;
  }
  if (typeof value === "function") return undefined;
  return value;
};

export const useRegisterSupportContext = (
  key: string,
  context: SupportScreenContext | null,
) => {
  const setContexts = useSetAtom(supportScreenContextAtom);
  const serialized = JSON.stringify(clean(context) ?? null);

  useEffect(() => {
    const value = JSON.parse(serialized) as SupportScreenContext | null;

    setContexts((current) => {
      const { [key]: _removed, ...rest } = current;
      return value ? { ...rest, [key]: value } : rest;
    });
  }, [key, serialized, setContexts]);

  useEffect(
    () => () =>
      setContexts((current) => {
        const { [key]: _removed, ...rest } = current;
        return rest;
      }),
    [key, setContexts],
  );
};

export const useSupportScreenContext = (): SupportScreenContext | null => {
  const contexts = useAtomValue(supportScreenContextAtom);

  return useMemo(() => {
    const entries = Object.values(contexts);
    if (!entries.length) return null;

    return entries.reduce<SupportScreenContext>(
      (merged, entry) => ({
        ...merged,
        ...entry,
        data: { ...merged.data, ...entry.data },
      }),
      {},
    );
  }, [contexts]);
};
